import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, LogOut } from 'lucide-react';

import { AppShell } from '@/components/layout/AppShell';
import { Avatar, Badge, Button, Card, HelpTip, PageContainer, PageHeader } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { ROLES_LABELS, type RoleType } from '@/lib/admin-types';
import { VERSION_FULL, VERSION_LABEL } from '@/version';
// S-2: a lista completa de departamentos virou admin-only; esta tela é de todo
// mundo e só precisa do nome do setor — daí o lookup (slug + nome).
import { useDepartmentsLookup } from '@/lib/admin-store';
import { MeusTestesCard } from '@/components/testes/MeusTestesCard';
import { MeuDesenvolvimentoCard } from '@/components/feedback/MeuDesenvolvimentoCard';

/**
 * /perfil — quem você é no sistema e o que fazer com isso.
 *
 * Read-only: foto e nome vêm do Google; vínculos e permissões são mexidos em
 * /admin/usuarios por um administrador.
 *
 * FASE-C: PageHeader canônico com h1 · ações viraram botões (eram links com
 * "→" na frente, que não pareciam clicáveis) · o campo "ID do usuário" com o
 * UUID em fonte mono saiu (identificador de banco não é informação de tela;
 * quem precisa dele é o admin, que o vê em /admin/usuarios).
 */
export function PerfilPage() {
  const { user, logout } = useAuth();
  const departments = useDepartmentsLookup();
  const [copiado, setCopiado] = useState(false);

  if (!user) {
    return (
      <AppShell>
        {/* LAYOUT-CANONICO: os dois wrappers desta tela (aviso de login e
            conteúdo) viraram a régua única; o `text-center` é layout interno e
            ficou no div de dentro. */}
        <PageContainer>
          <div className="text-center">
            <p className="text-sm text-ink-2 dark:text-ink-dark-2">
              Faça login pra acessar seu perfil.
            </p>
          </div>
        </PageContainer>
      </AppShell>
    );
  }

  const departmentLabel = (slug: string) =>
    departments.find((d) => d.slug === slug)?.nome ?? slug;

  // FE2: link de captação — só p/ quem é do comercial e tem slug.
  const ehComercial = user.departamentos.some((d) => d.departmentSlug === 'comercial');
  const mostrarLinkCaptacao = ehComercial && !!user.slug;
  const linkCaptacao = user.slug ? `${window.location.origin}/f/${user.slug}` : '';

  async function copiarLink() {
    try {
      await navigator.clipboard.writeText(linkCaptacao);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // clipboard indisponível (http/permissão negada) — ignora silenciosamente.
    }
  }

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          crumb="Conta"
          title="Meu perfil"
          subtitle="Seus dados de acesso e os departamentos em que você trabalha. Foto e nome vêm da conta Google e se atualizam a cada login."
        />

        {/* Identidade + ações */}
        <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card variant="flat" className="p-6 lg:col-span-2">
            <div className="flex flex-col items-start gap-5 sm:flex-row">
              <Avatar name={user.name} src={user.fotoUrl} size="2xl" />
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <h2 className="font-display text-2xl tracking-tight text-ink-1 dark:text-ink-dark-1 md:text-3xl">
                    {user.name}
                  </h2>
                  {user.isAdmin && (
                    <Badge variant="info" size="sm">
                      Administrador
                    </Badge>
                  )}
                </div>
                <p className="break-all text-sm text-ink-2 dark:text-ink-dark-2">{user.email}</p>
                <p className="mt-3 text-xs text-ink-3 dark:text-ink-dark-3">
                  Para trocar foto ou nome, atualize na sua conta Google — muda aqui no
                  próximo login.
                </p>
              </div>
            </div>
          </Card>

          <Card variant="flat" className="flex flex-col gap-2 p-6">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
              Ações
            </p>
            <Link to="/perfil/notificacoes" className="block">
              <Button
                variant="secondary"
                fullWidth
                leftIcon={<Bell className="h-4 w-4" aria-hidden />}
              >
                Preferências de notificação
              </Button>
            </Link>
            <Button
              variant="ghost"
              fullWidth
              onClick={logout}
              leftIcon={<LogOut className="h-4 w-4" aria-hidden />}
              data-testid="perfil-sair"
            >
              Sair da conta
            </Button>
          </Card>
        </div>

        {/* FE2: Meu link de captação (só comercial com slug) */}
        {mostrarLinkCaptacao && (
          <Card variant="flat" className="mb-4 p-6">
            <div className="mb-2 flex items-center gap-1.5">
              <h2 className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1">
                Meu link de captação
              </h2>
              <HelpTip texto="Compartilhe este link (no Instagram, WhatsApp, cartão de visita). Quem preencher o formulário vira um lead já atribuído a você, com origem Formulário." />
            </div>
            <p className="mb-3 text-sm leading-relaxed text-ink-2 dark:text-ink-dark-2">
              Envie este endereço para receber pedidos de contato direto no seu funil. O
              lead já entra no seu nome.
            </p>
            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
              <code className="min-w-0 flex-1 truncate rounded-lg bg-paper-warm px-3 py-2 font-mono text-xs text-ink-1 dark:bg-paper-dark-warm dark:text-ink-dark-1">
                {linkCaptacao}
              </code>
              <Button variant="secondary" size="sm" onClick={copiarLink} className="shrink-0">
                {copiado ? 'Copiado!' : 'Copiar'}
              </Button>
            </div>
          </Card>
        )}

        {/* TESTES DISC e QA (T7a): os próprios resultados — só para quem tem ficha de funcionário. */}
        <MeusTestesCard />

        {/* FEEDBACK (C1): o plano e os compromissos publicados — só para quem tem ficha de funcionário. */}
        <MeuDesenvolvimentoCard />

        {/* Departamentos */}
        <Card variant="flat" className="mb-6 p-6">
          <h2 className="mb-3 text-sm font-semibold text-ink-1 dark:text-ink-dark-1">
            Onde você trabalha
          </h2>
          {user.departamentos.length === 0 ? (
            <p className="text-sm text-ink-2 dark:text-ink-dark-2">
              Você ainda não está ligado a nenhum departamento — por isso algumas telas
              não aparecem no menu. Peça ao administrador para incluir você.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {user.departamentos.map((dr) => (
                <div
                  key={dr.departmentSlug}
                  className="flex items-center justify-between gap-3 rounded-lg bg-paper-warm p-3 dark:bg-paper-dark-warm"
                >
                  <span className="min-w-0 truncate text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                    {departmentLabel(dr.departmentSlug)}
                  </span>
                  <Badge variant={badgeVariantForRole(dr.role)} size="sm">
                    {ROLES_LABELS[dr.role]?.label ?? dr.role}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* FASE-B: a marca de versão saiu do overlay flutuante (aparecia em
            todas as telas por cima do conteúdo) e mora aqui, estática. */}
        <div
          className="pt-2 text-xs text-ink-3 dark:text-ink-dark-3"
          data-testid="perfil-versao"
          title={VERSION_FULL}
        >
          IGM Security · {VERSION_LABEL}
        </div>
      </PageContainer>
    </AppShell>
  );
}

function badgeVariantForRole(role: RoleType): 'success' | 'info' | 'neutral' {
  if (role === 'lider') return 'success';
  if (role === 'colaborador') return 'info';
  return 'neutral';
}
